import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, TrendingUp, ClipboardList, Users, Award, GraduationCap, Briefcase, Network, Wallet, BarChart3, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { ProjectsManager, DealersManager, ContentManager, FaqManager } from "@/pages/admin/AdminCMS";

const STAGES = ["Enquiry received", "Assessment pending", "Quote prepared", "Quote accepted", "Scheduled",
  "Technician assigned", "Installation in progress", "QC", "Completed", "Warranty activated"];
const LEAD_STAGES = ["New", "Contacted", "Site Visit", "Quote Sent", "Follow-up", "Won", "Lost"];

const APPLICATOR_LINKS = [
  ["Applications", "/admin/applicators", ClipboardList],
  ["Applicators", "/admin/applicators", Users],
  ["Services & Certifications", "/admin/applicator-services", Award],
  ["Training & Courses", "/admin/training", GraduationCap],
  ["Jobs", "/admin/applicator-jobs", Briefcase],
  ["Network", "/admin/applicators", Network],
  ["Earnings / Payouts", "/admin/applicator-payouts", Wallet],
  ["Analytics", "/admin/analytics", BarChart3],
  ["Communications", "/admin/communications", Network],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [appSummary, setAppSummary] = useState(null);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/admin/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
    api.get("/admin/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/admin/partners").then(({ data }) => setPartners(data)).catch(() => {});
    api.get("/admin/applicator-summary").then(({ data }) => setAppSummary(data)).catch(() => {});
  };
  useEffect(load, []);

  const saveQuote = async (id, body) => { await api.put(`/admin/quotes/${id}`, body); toast.success("Quote updated"); load(); };
  const assign = async (id, partner_id) => { await api.put(`/admin/bookings/${id}/assign`, { partner_id }); toast.success("Assigned to partner"); load(); };

  const tabs = ["overview", "applicator network", "leads", "quotes", "bookings", "customers", "partners", "projects", "dealers", "content", "faqs"];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard-page">
      <Seo title="Admin CRM" description="ERRAH admin CRM." />
      <header className="glass sticky top-0 z-30 px-5 lg:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3"><img src="/errah-logo.png" alt="ERRAH" className="h-9 w-auto object-contain" /><span className="font-display text-xl tracking-[0.2em]">ERRAH · ADMIN</span></div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400 hidden sm:block">{user?.email}</span>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"><LogOut size={16} /> Logout</button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8">
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-8">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.replace(/ /g, "-")}`}
              className={`px-4 py-3 text-sm capitalize border-b-2 -mb-px transition-colors ${tab === t ? "border-[#C9A45C] text-white" : "border-transparent text-gray-500 hover:text-gray-300"}`}>{t}</button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
              {[["Leads", stats.leads], ["AI Scans", stats.scans], ["Quotes", stats.quotes], ["Bookings", stats.bookings],
                ["Customers", stats.customers], ["Partners", stats.partners], ["Warranties", stats.warranties], ["Conversion", `${stats.conversion}%`]].map(([k, v]) => (
                <div key={k} className="bg-[#0A0A0A] p-6"><p className="errah-label">{k}</p><p className="font-display text-3xl mt-1">{v}</p></div>
              ))}
            </div>
            <div className="mt-8 hair-border bg-white/[0.02] p-6">
              <div className="flex items-center gap-2 mb-6"><TrendingUp size={18} className="text-[#C9A45C]" /><h3 className="font-display text-lg">Leads by source</h3></div>
              {stats.sources.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={stats.sources}>
                    <XAxis dataKey="source" stroke="#666" fontSize={12} />
                    <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #333", color: "#fff" }} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
                    <Bar dataKey="count">{stats.sources.map((_, i) => <Cell key={i} fill="#C9A45C" />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-500 text-sm">No lead data yet.</p>}
            </div>

            {appSummary && (
              <div className="mt-8 hair-border bg-white/[0.02] p-6" data-testid="overview-applicator-summary">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2"><Network size={18} className="text-[#C9A45C]" /><h3 className="font-display text-lg">Applicator Network</h3></div>
                  <button onClick={() => setTab("applicator network")} data-testid="manage-applicators-btn" className="inline-flex items-center gap-1.5 bg-[#C9A45C] text-black px-4 py-2 text-xs font-semibold hover:bg-[#E4C987] transition-colors">Manage Applicators <ArrowRight size={14} /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10">
                  {[["Pending Applications", appSummary.pending_applications], ["Active Applicators", appSummary.active_applicators], ["Certified", appSummary.certified_applicators], ["In Training", appSummary.in_training], ["Open Jobs", appSummary.open_jobs]].map(([k, v]) => (
                    <div key={k} className="bg-[#0A0A0A] p-5"><p className="errah-label">{k}</p><p className="font-display text-2xl mt-1 gold-text">{v}</p></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "applicator network" && (
          <div data-testid="admin-applicator-network">
            {appSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 mb-8">
                {[["Pending", appSummary.pending_applications], ["Active", appSummary.active_applicators], ["Certified", appSummary.certified_applicators], ["In Training", appSummary.in_training], ["Open Jobs", appSummary.open_jobs]].map(([k, v]) => (
                  <div key={k} className="bg-[#0A0A0A] p-5"><p className="errah-label">{k}</p><p className="font-display text-2xl mt-1 gold-text">{v}</p></div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {APPLICATOR_LINKS.map(([label, to, Icon]) => (
                <Link key={label} to={to} data-testid={`applicator-hub-${label.split(" ")[0].toLowerCase()}`} className="hair-border bg-white/[0.02] p-5 hover:bg-white/[0.05] hover:border-[#C9A45C]/40 transition-colors group">
                  <Icon className="text-[#C9A45C]" size={22} />
                  <p className="mt-3 font-display text-sm">{label}</p>
                  <p className="mt-1 text-xs text-gray-500 inline-flex items-center gap-1 group-hover:text-[#C9A45C]">Open <ArrowRight size={12} /></p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <Table testid="admin-leads" head={["Name", "Type", "Contact", "Surface", "Source", "Date"]}
            rows={leads.map((l) => [l.name, l.type, l.mobile || l.email || "—", l.surface_type || "—", l.source, (l.created_at || "").slice(0, 10)])} />
        )}

        {tab === "quotes" && (
          <div data-testid="admin-quotes" className="space-y-3">
            {quotes.map((q) => (
              <div key={q.id} className="hair-border bg-white/[0.02] p-5" data-testid={`admin-quote-${q.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><h3 className="font-display text-base">{q.name} · {q.surface_type || "Surface"}</h3><p className="text-sm text-gray-500">{q.mobile} · {q.property_type} · {q.location || "—"} {q.area && `· ~${q.area} sq ft`}</p></div>
                  <span className="px-3 py-1 text-xs border border-white/20 text-gray-300">{q.status}</span>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-2">
                  <div>
                    <label className="errah-label !text-[9px] block mb-1">Lead stage</label>
                    <select defaultValue={q.stage || q.status || "New"} onChange={(e) => saveQuote(q.id, { stage: e.target.value, status: e.target.value })} data-testid={`quote-stage-${q.id}`} className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 text-sm [color-scheme:dark]">
                      {LEAD_STAGES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="errah-label !text-[9px] block mb-1">Estimate</label>
                    <input placeholder="₹18,000–₹24,000" defaultValue={q.estimate || ""} onBlur={(e) => e.target.value !== (q.estimate || "") && saveQuote(q.id, { estimate: e.target.value })} data-testid={`quote-estimate-${q.id}`} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#C9A45C]" />
                  </div>
                  <div>
                    <label className="errah-label !text-[9px] block mb-1">Assigned to</label>
                    <input placeholder="Employee / partner" defaultValue={q.assigned_to || ""} onBlur={(e) => e.target.value !== (q.assigned_to || "") && saveQuote(q.id, { assigned_to: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#C9A45C]" />
                  </div>
                  <div>
                    <label className="errah-label !text-[9px] block mb-1">Follow-up date</label>
                    <input type="date" defaultValue={q.followup_date || ""} onChange={(e) => saveQuote(q.id, { followup_date: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 text-sm [color-scheme:dark]" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="errah-label !text-[9px] block mb-1">Notes · Source: {q.source || "Website"}</label>
                    <input placeholder="Add a note…" defaultValue={q.notes || ""} onBlur={(e) => e.target.value !== (q.notes || "") && saveQuote(q.id, { notes: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#C9A45C]" />
                  </div>
                </div>
              </div>
            ))}
            {quotes.length === 0 && <p className="text-gray-500">No quotes yet.</p>}
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="admin-bookings" className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="hair-border bg-white/[0.02] p-5" data-testid={`admin-booking-${b.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><h3 className="font-display text-base">{b.customer_name} · {b.service}</h3><p className="text-sm text-gray-500">{b.surface_type} · {b.location || "—"} · {b.preferred_date || "TBD"}</p></div>
                  <span className="px-3 py-1 text-xs text-[#C9A45C] border border-[#C9A45C]/40">{b.status}</span>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="errah-label">Assign partner</span>
                  <select defaultValue={b.partner_id || ""} onChange={(e) => assign(b.id, e.target.value)} data-testid={`assign-${b.id}`} className="bg-[#0A0A0A] border border-white/15 px-3 py-2 text-sm [color-scheme:dark]">
                    <option value="">Unassigned</option>
                    {partners.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
            ))}
            {bookings.length === 0 && <p className="text-gray-500">No bookings yet.</p>}
          </div>
        )}

        {tab === "customers" && (
          <Table testid="admin-customers" head={["Name", "Email", "Phone", "Joined"]}
            rows={customers.map((c) => [c.name, c.email, c.phone || "—", (c.created_at || "").slice(0, 10)])} />
        )}
        {tab === "partners" && (
          <Table testid="admin-partners" head={["Name", "Email", "Phone"]}
            rows={partners.map((p) => [p.name, p.email, p.phone || "—"])} />
        )}
        {tab === "projects" && <ProjectsManager />}
        {tab === "dealers" && <DealersManager />}
        {tab === "content" && <ContentManager />}
        {tab === "faqs" && <FaqManager />}
      </div>
    </div>
  );
}

function Table({ head, rows, testid }) {
  return (
    <div className="hair-border overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-white/10">{head.map((h) => <th key={h} className="text-left px-4 py-3 errah-label">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
              {r.map((c, j) => <td key={j} className="px-4 py-3 text-gray-300">{c}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={head.length} className="px-4 py-8 text-center text-gray-600">No records.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
